import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Eye, Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Dropzone } from "@/components/ui/customs/dropzone-file";
import { useCreateProduct, useUpdateProduct } from "@/api/product.api";
import { useCreateProductUsa, useUpdateProductUsa } from "@/api/productUsa.api";
import { useGetAllImporters } from "@/api/importer.api";
import { useGetCompanyBoListV1 } from "@/api/company.api";
import { useCreateFileBo } from "@/api/file.api";
import { useMutateGetFile } from "@/api/s3.api";
import { ProductDto, ProductUSA } from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { volumesOptionsCola } from "@/constants/options/volumes.option";
import {
  productFormSchema,
  productUsaFormSchema,
  type ProductFormData,
  type ProductUsaFormData,
} from "@/components/backOffice/products/productFormSchema";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { useTranslation } from "react-i18next";

type ProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  isUSA: boolean;
  product?: ProductDto | ProductUSA | null;
  onProductSaved?: (productId: string) => void;
  importerId?: string;
  clientId?: string;
};

export const ProductDrawer = ({
  open,
  onClose,
  isUSA,
  product,
  onProductSaved,
  importerId,
  clientId,
}: ProductDrawerProps) => {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "bg-background fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l shadow-xl duration-300",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          )}
        >
          <DialogPrimitive.Close className="absolute top-4 right-4 z-10 rounded-sm opacity-70 transition-opacity hover:opacity-100">
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>

          {isUSA ? (
            <ProductUsaDrawerForm
              product={product as ProductUSA | null | undefined}
              onClose={onClose}
              onProductSaved={onProductSaved}
              open={open}
              importerId={importerId}
              clientId={clientId}
            />
          ) : (
            <ProductStandardDrawerForm
              product={product as ProductDto | null | undefined}
              onClose={onClose}
              onProductSaved={onProductSaved}
              open={open}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

/* -------------------------------------------------------------------------- */
/*  Standard Product form                                                     */
/* -------------------------------------------------------------------------- */

type StandardFormProps = {
  open: boolean;
  onClose: () => void;
  product?: ProductDto | null;
  onProductSaved?: (productId: string) => void;
};

const ProductStandardDrawerForm = ({
  open,
  onClose,
  product,
  onProductSaved,
}: StandardFormProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const wineColorOptions = useWineColorOptions();
  const isEdit = !!product;

  const { mutateAsync: createProduct, isPending: isCreating } = useCreateProduct();
  const { mutateAsync: updateProduct, isPending: isUpdating } = useUpdateProduct();
  const isPending = isCreating || isUpdating;

  const { control, handleSubmit, reset } = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      description: "",
      descriptionPelican: "",
      countryOfOrigin: "",
      harmonizedCode: "",
      color: undefined as unknown as WineColorEnum,
      volume: 0,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (product) {
      reset({
        description: product.description,
        descriptionPelican: product.descriptionPelican,
        countryOfOrigin: product.countryOfOrigin,
        harmonizedCode: product.harmonizedCode,
        color: product.color as WineColorEnum,
        volume: product.volume,
      });
    } else {
      reset({
        description: "",
        descriptionPelican: "",
        countryOfOrigin: "",
        harmonizedCode: "",
        color: undefined as unknown as WineColorEnum,
        volume: 0,
      });
    }
  }, [open, product, reset]);

  const onSubmit = async (data: ProductFormData) => {
    if (isEdit) {
      await updateProduct({ id: product.id, payload: data });
      onProductSaved?.(product.id);
    } else {
      const result = await createProduct(data);
      const newId = result?.data?.id;
      if (newId) onProductSaved?.(newId);
    }
    onClose();
  };

  return (
    <form
      onSubmit={(e) => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
      className="flex h-full flex-col"
    >
      <div className="border-b px-6 py-4">
        <DialogPrimitive.Title className="text-lg font-semibold">
          {isEdit ? t("productDrawer.editTitle") : t("productDrawer.createTitle")}
        </DialogPrimitive.Title>
        <DialogPrimitive.Description className="text-sm text-gray-500">
          {isEdit ? t("productDrawer.editDescription") : t("productDrawer.createDescription")}
        </DialogPrimitive.Description>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-2 gap-4">
          <ControlledUnifiedInput
            control={control}
            name="description"
            label={t("productDrawer.descriptionLabel")}
            wrapperClassName="col-span-2"
          />
          <ControlledUnifiedInput
            control={control}
            name="descriptionPelican"
            label={t("productDrawer.descriptionPelicanLabel")}
            wrapperClassName="col-span-2"
          />
          <ControlledCountryComboboxCountryCode
            control={control}
            name="countryOfOrigin"
            label={t("productDrawer.countryOfOriginLabel")}
          />
          <ControlledUnifiedInput
            control={control}
            name="harmonizedCode"
            label={t("productDrawer.harmonizedCodeLabel")}
          />
          <ControlledSelectV2
            control={control}
            name="color"
            label={t("productDrawer.colorLabel")}
            options={wineColorOptions}
            placeholder=""
            wrapperClassName="col-span-2"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t px-6 py-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? (
            <Loader2 className="animate-spin" />
          ) : isEdit ? (
            t("productDrawer.save")
          ) : (
            t("productDrawer.create")
          )}
        </Button>
      </div>
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/*  USA Product form                                                          */
/* -------------------------------------------------------------------------- */

type UsaFormProps = {
  open: boolean;
  onClose: () => void;
  product?: ProductUSA | null;
  onProductSaved?: (productId: string) => void;
  importerId?: string;
  clientId?: string;
};

const ProductUsaDrawerForm = ({
  open,
  onClose,
  product,
  onProductSaved,
  importerId,
  clientId,
}: UsaFormProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const translateMessage = useTranslateMessage();
  const wineColorOptions = useWineColorOptions();
  const isEdit = !!product;

  const { mutateAsync: createProductUsa, isPending: isCreating } = useCreateProductUsa();
  const { mutateAsync: updateProductUsa, isPending: isUpdating } = useUpdateProductUsa();
  const { mutateAsync: createFile } = useCreateFileBo();
  const { mutateAsync: getFileUrl, isPending: isLoadingFile } = useMutateGetFile();
  const { data: importers } = useGetAllImporters();
  const { data: companies } = useGetCompanyBoListV1();

  const [colaFiles, setColaFiles] = useState<File[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const isPending = isCreating || isUpdating || isUploading;

  const importerOptions = useMemo(() => {
    const all = (importers?.data || []).map((i) => ({ label: i.name, value: i.id }));
    const filtered = all.filter((o) => o.value === importerId);
    return filtered.length > 0 ? filtered : [];
  }, [importers, importerId]);

  const companyOptions = useMemo(() => {
    const all = (companies?.data || []).map((c) => ({ label: c.businessName, value: c.id }));
    const filtered = all.filter((o) => o.value === clientId);
    return filtered.length > 0 ? filtered : [];
  }, [companies?.data, clientId]);

  const volumeSelectOptions = useMemo(
    () => volumesOptionsCola.map((v) => ({ label: v.label, value: String(v.value) })),
    [],
  );

  const { control, handleSubmit, reset, setValue, formState } = useForm<ProductUsaFormData>({
    resolver: zodResolver(productUsaFormSchema),
    defaultValues: {
      description: "",
      descriptionPelican: "",
      countryOfOrigin: "",
      harmonizedCode: "",
      color: undefined as unknown as WineColorEnum,
      alcoholPercentage: undefined,
      volume: undefined,
      ttbNumber: "",
      importerId: "",
      clientId: "",
      colaId: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    setColaFiles(null);
    if (product) {
      reset({
        description: product.description,
        descriptionPelican: product.descriptionPelican,
        countryOfOrigin: product.countryOfOrigin,
        harmonizedCode: product.harmonizedCode,
        color: product.color as WineColorEnum,
        alcoholPercentage: product.alcoholPercentage,
        volume: product.volume,
        ttbNumber: product.ttbNumber,
        importerId: product.importer.id,
        clientId: product.client?.id || "",
        colaId: product.cola.id,
      });
    } else {
      reset({
        description: "",
        descriptionPelican: "",
        countryOfOrigin: "",
        harmonizedCode: "",
        color: undefined as unknown as WineColorEnum,
        alcoholPercentage: undefined,
        volume: undefined,
        ttbNumber: "",
        importerId: importerId,
        clientId: "",
        colaId: "",
      });
    }
  }, [open, product, reset, importerId, clientId]);

  const uploadColaFile = async (file: File): Promise<string> => {
    const contentType = file.type || "application/pdf";
    const { uploadUrl, fileId } = await createFile({
      fileName: file.name,
      type: FileTypeEnum.PRODUCT_COLA,
      contentType,
    });
    await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
    return fileId;
  };

  const onSubmit = async (data: ProductUsaFormData) => {
    try {
      setIsUploading(true);
      let colaId = data.colaId;
      if (colaFiles && colaFiles.length > 0) {
        colaId = await uploadColaFile(colaFiles[0]);
      }

      const payload = {
        ...data,
        clientId: data.clientId || "",
        colaId,
      };

      if (isEdit) {
        await updateProductUsa({ id: product.id, payload });
        onProductSaved?.(product.id);
      } else {
        const result = await createProductUsa(payload);
        const newId = result?.data?.id;
        if (newId) onProductSaved?.(newId);
      }
      onClose();
    } catch {
      toast.error(t("productDrawer.colaUploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleColaFilesChange = (
    files: File[] | null | ((prev: File[] | null) => File[] | null),
  ) => {
    const resolved = typeof files === "function" ? files(colaFiles) : files;
    setColaFiles(resolved);
    if (resolved && resolved.length > 0) {
      setValue("colaId", "pending-upload", { shouldValidate: true });
    } else {
      setValue("colaId", "", { shouldValidate: true });
    }
  };

  const handleViewCola = async () => {
    if (!product?.cola?.path) return;
    const url = await getFileUrl(product.cola.path);
    if (url) window.open(url, "_blank");
  };

  const hasExistingCola = isEdit && !!product?.cola?.path && !colaFiles?.length;

  return (
    <form
      onSubmit={(e) => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
      className="flex h-full flex-col"
    >
      <div className="border-b px-6 py-4">
        <DialogPrimitive.Title className="text-lg font-semibold">
          {isEdit ? t("productDrawer.usaEditTitle") : t("productDrawer.usaCreateTitle")}
        </DialogPrimitive.Title>
        <DialogPrimitive.Description className="text-sm text-gray-500">
          {isEdit ? t("productDrawer.usaEditDescription") : t("productDrawer.usaCreateDescription")}
        </DialogPrimitive.Description>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-2 gap-4">
          <ControlledUnifiedInput
            control={control}
            name="description"
            label={t("productDrawer.descriptionLabel")}
            wrapperClassName="col-span-2"
          />
          <ControlledUnifiedInput
            control={control}
            name="descriptionPelican"
            label={t("productDrawer.descriptionPelicanLabel")}
            wrapperClassName="col-span-2"
          />
          <ControlledCountryComboboxCountryCode
            control={control}
            name="countryOfOrigin"
            label={t("productDrawer.countryOfOriginLabel")}
          />
          <ControlledUnifiedInput
            control={control}
            name="harmonizedCode"
            label={t("productDrawer.harmonizedCodeLabel")}
          />
          <ControlledSelectV2
            control={control}
            name="color"
            label={t("productDrawer.colorLabel")}
            options={wineColorOptions}
            placeholder=""
          />
          <ControlledSelectV2
            control={control}
            name="volume"
            label={t("productDrawer.volumeLabel")}
            options={volumeSelectOptions}
            setValue={(value) => setValue("volume", parseFloat(value))}
            placeholder=""
          />
          <ControlledUnifiedInput
            control={control}
            name="alcoholPercentage"
            label={t("productDrawer.alcoholPercentageLabel")}
            inputType="float"
          />
          <ControlledUnifiedInput
            control={control}
            name="ttbNumber"
            label={t("productDrawer.ttbNumberLabel")}
          />
          <div className="col-span-2 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("productDrawer.colaDocumentLabel")}</span>
              {hasExistingCola && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 max-w-[300px] gap-1 text-xs text-blue-600 hover:text-blue-700"
                  onClick={handleViewCola}
                  disabled={isLoadingFile}
                >
                  {isLoadingFile ? (
                    <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                  ) : (
                    <Eye className="h-3 w-3 shrink-0" />
                  )}
                  <span className="truncate">
                    {product.cola.name || t("productDrawer.viewCola")}
                  </span>
                </Button>
              )}
            </div>
            <Dropzone
              files={colaFiles}
              setFiles={handleColaFilesChange}
              maxFiles={1}
              dropZoneText={
                hasExistingCola
                  ? t("productDrawer.replaceColaDropzone")
                  : t("productDrawer.colaDropzone")
              }
              accept={{ "application/pdf": [".pdf"] }}
            />
            {formState.errors.colaId && !colaFiles?.length && (
              <p className="text-xs text-red-500">
                {translateMessage(formState.errors.colaId.message)}
              </p>
            )}
          </div>
          <ControlledSelectV2
            control={control}
            name="importerId"
            label={t("productDrawer.importerLabel")}
            options={importerOptions}
            wrapperClassName="col-span-2"
            placeholder=""
            isDisabled={true}
          />
          <ControlledSelectV2
            control={control}
            name="clientId"
            label={t("productDrawer.clientLabel")}
            options={companyOptions}
            clearable
            wrapperClassName="col-span-2"
            placeholder=""
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t px-6 py-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? (
            <Loader2 className="animate-spin" />
          ) : isEdit ? (
            t("productDrawer.save")
          ) : (
            t("productDrawer.create")
          )}
        </Button>
      </div>
    </form>
  );
};
